import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { cn } from "../client/utils";
import { SCAN_STEPS } from "./scanSteps";
import type { ScanContext } from "./useEdgyAnalysis";

/**
 * The "scan" moment. Steps are ticked off by REAL SSE events — the numbers shown come
 * from context_ready, never invented. When the API is slower than the sequence, the
 * current step keeps spinning rather than advancing on a timer.
 */

/** Counts up to a target; skipped entirely under reduced motion. */
function AnimatedCount({ value, reduced }: { value: number; reduced: boolean }) {
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return;
    }
    let frame: number;
    const start = performance.now();
    const duration = 900;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, reduced]);

  return <span className="font-mono tabular-nums">{display}</span>;
}

export default function EdgyScanSequence({
  currentStep,
  context,
  strategyName,
}: {
  currentStep: number;
  context: ScanContext | null;
  strategyName: string;
}) {
  const t = useTranslation();
  const reduced = useReducedMotion() ?? false;

  const labels: Record<string, string> = {
    reading: t.edgy.scanning.reading,
    sessions: t.edgy.scanning.sessions,
    playbook: t.edgy.scanning.playbook,
    leaks: t.edgy.scanning.leaks,
    writing: t.edgy.scanning.writing,
  };

  return (
    <div className="flex flex-col gap-3 py-2">
      {SCAN_STEPS.map((step, index) => {
        const isDone = index < currentStep;
        const isActive = index === currentStep;
        const isPending = index > currentStep;

        let label = labels[step];
        if (step === "reading") {
          // The count only arrives with context_ready. Until then the raw
          // "{trades}" placeholder was rendered as-is; drop it instead.
          label = context
            ? label.replace("{trades}", String(context.tradesCount))
            : label.replace("{trades} ", "").replace("{trades}", "");
        }
        if (step === "playbook") {
          label = `${label} ${strategyName}`.trim();
        }

        return (
          <motion.div
            key={step}
            initial={reduced ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: isPending ? 0.35 : 1, y: 0 }}
            transition={{ duration: 0.35, delay: reduced ? 0 : index * 0.05 }}
            className="flex items-center gap-3"
          >
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
                isDone && "border-emerald-500 bg-emerald-500 text-white",
                isActive && "border-emerald-500 text-emerald-500",
                isPending && "border-border text-muted-foreground"
              )}
            >
              {isDone ? (
                <Check className="h-3.5 w-3.5" />
              ) : isActive ? (
                <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
            </span>

            <span
              className={cn(
                "text-sm",
                isDone && "text-muted-foreground",
                isActive && "font-medium text-foreground",
                isPending && "text-muted-foreground"
              )}
            >
              {step === "reading" && context ? (
                <>
                  {labels.reading.split("{trades}")[0]}
                  <AnimatedCount value={context.tradesCount} reduced={reduced} />
                  {labels.reading.split("{trades}")[1]}
                </>
              ) : (
                label
              )}
            </span>
          </motion.div>
        );
      })}

      {context?.lowSample && (
        <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          {t.edgy.reveal.lowSampleNotice}
        </p>
      )}
    </div>
  );
}
